/**
 * The web-client logger — one implementation for every delivery mode (iframe
 * app, npm lib, SDK). Lives in `src/core` because that is the only directory
 * compiled into both the lib and the SDK builds; `src/logger.ts` and
 * `src/lib/logger.ts` are thin re-exports so each build keeps its own entry
 * point without a second copy of the resolution rules.
 *
 * Level resolution. The initial level is seeded, highest wins:
 *   1. `?log=` URL param     — per-session opt-in, survives reloads
 *   2. `window.__AQ_CONFIG`  — per-environment, rendered by `/config.js`
 *   3. compiled default      — LOG_LEVEL / NODE_ENV baked at build time
 *
 * `__aqLog.setLevel()` then overrides whatever was seeded, from the moment it
 * is called — the latest explicit human action wins. It deliberately outranks
 * `?log=`: the scenario the live setter exists for is a misbehaving session on
 * a URL that already carries `?log=info`, where the only alternative is a
 * session-destroying reload.
 *
 * The compiled value is the last resort, not a ceiling: raising the level at
 * runtime works in any build.
 */

import { readRuntimeConfig, type LogLevel } from './app-config.js';

export type { LogLevel };

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LEVEL_NAMES = Object.keys(LOG_LEVELS) as LogLevel[];

export function isLogLevel(value: unknown): value is LogLevel {
  return typeof value === 'string' && value in LOG_LEVELS;
}

function urlLevel(): LogLevel | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = new URLSearchParams(window.location.search).get('log')?.toLowerCase();
    return isLogLevel(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Read one build-time value, tolerating the absence of `process`.
 *
 * Each read gets its own try/catch instead of a single
 * `typeof process !== 'undefined'` gate around the lot. Bundlers substitute
 * the exact `process.env.X` expressions, but such a gate survives verbatim —
 * and in a browser bundle `process` genuinely does not exist, so the gate
 * skips the branch and throws away the literal that was just substituted into
 * it. (That is not hypothetical: it silently pinned the app bundle to `info`.)
 * Reading directly keeps the substituted literal reachable, and the catch
 * still covers consumer bundles that substitute nothing.
 */
function readBuildEnv(read: () => string | undefined): string | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

function compiledLevel(): LogLevel {
  const explicit = readBuildEnv(() => process.env.LOG_LEVEL)?.toLowerCase();
  if (isLogLevel(explicit)) {
    return explicit;
  }
  if (readBuildEnv(() => process.env.NODE_ENV) === 'production') {
    return 'warn';
  }
  return 'info';
}

function seedLevel(): LogLevel {
  const fromUrl = urlLevel();
  if (fromUrl) {
    return fromUrl;
  }
  const fromRuntime = readRuntimeConfig().logLevel;
  return isLogLevel(fromRuntime) ? fromRuntime : compiledLevel();
}

let currentLevel: number = LOG_LEVELS[seedLevel()];

function setLevel(next: unknown): void {
  const level = typeof next === 'string' ? next.toLowerCase() : next;
  if (!isLogLevel(level)) {
    // eslint-disable-next-line no-console
    console.warn(`[aqLog] Unknown level "${String(next)}". Use one of: ${LEVEL_NAMES.join(', ')}`);
    return;
  }
  currentLevel = LOG_LEVELS[level];
}

/** The level in effect right now, after the full precedence chain. */
export function getLogLevel(): LogLevel {
  return LEVEL_NAMES.find((name) => LOG_LEVELS[name] === currentLevel) ?? 'info';
}

/** Console handle for raising verbosity mid-session: `__aqLog.setLevel('debug')`. */
export interface AqLogControl {
  setLevel(level: string): void;
  getLevel(): LogLevel;
  readonly levels: readonly LogLevel[];
}

declare global {
  interface Window {
    __aqLog?: AqLogControl;
  }
}

if (typeof window !== 'undefined') {
  window.__aqLog = { setLevel, getLevel: getLogLevel, levels: LEVEL_NAMES };
}

export const getLogger = (name: string) => ({
  debug: (...args: any[]) => {
    if (currentLevel <= LOG_LEVELS.debug) {
      console.debug(`[${name}]`, ...args);
    }
  },
  info: (...args: any[]) => {
    if (currentLevel <= LOG_LEVELS.info) {
      console.info(`[${name}]`, ...args);
    }
  },
  warn: (...args: any[]) => {
    if (currentLevel <= LOG_LEVELS.warn) {
      console.warn(`[${name}]`, ...args);
    }
  },
  error: (...args: any[]) => {
    if (currentLevel <= LOG_LEVELS.error) {
      console.error(`[${name}]`, ...args);
    }
  },
});
